/**
 * KidsMenu.jsx - Kids Paradise Navigation Menu
 * 
 * STANDARDIZATION MANDATE (2026-04-25):
 * - Must look and behave EXACTLY like the QuranPage menu
 * - Feature parity with QuranPage menu (styling, layout, interactions)
 * - Inherits structure from GlobalMenu but with kids-specific theming
 * 
 * KIDS MENU ORDER:
 * 1. Home
 * 2. Quran
 * 3. Prayer Times
 * 4. Duas
 * 5. Learn
 * 6. Stories (Bedtime Stories) - NEW
 * 7. Tajweed Coach (PRO)
 * 8. Halal Scanner (PRO)
 * 9. Progress (PRO)
 * 10. Zoom (PRO)
 * 11. Qibla
 * 12. Saved
 * 13. Search
 * 14. Settings
 */

import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Home, BookOpen, Clock, HandHeart, GraduationCap, 
  Scan, TrendingUp, ZoomIn, Compass, Bookmark, 
  Search, Settings, Crown, Star, Sparkles, BookHeart
} from 'lucide-react';
import { usePro } from '../../contexts/ProContext';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

/**
 * Get Kids Paradise specific navigation items
 * Matches QuranPage menu structure and styling
 */
export const getKidsNavItems = (t) => {
  return [
    { 
      path: '/', 
      label: t('nav.home', 'Home'), 
      icon: Home, 
      id: 'home',
      kidsLabel: t('kidsNav.home', 'My Home')
    },
    { 
      path: '/quran', 
      label: t('nav.quran', 'Quran'), 
      icon: BookOpen, 
      id: 'quran',
      kidsLabel: t('kidsNav.quran', 'Read Quran')
    },
    { 
      path: '/prayer-times', 
      label: t('nav.prayerTimes', 'Prayer Times'), 
      icon: Clock, 
      id: 'prayer',
      kidsLabel: t('kidsNav.prayerTimes', 'Prayer Time')
    },
    { 
      path: '/duas', 
      label: t('nav.duas', 'Duas'), 
      icon: HandHeart, 
      id: 'duas',
      kidsLabel: t('kidsNav.duas', 'My Duas')
    },
    { 
      path: '/learn', 
      label: t('nav.learn', 'Learn'), 
      icon: GraduationCap, 
      id: 'learn',
      kidsLabel: t('kidsNav.learn', 'Fun Learning')
    },
    { 
      path: '/daily-stories', 
      label: t('nav.stories', 'Stories'), 
      icon: BookHeart, 
      id: 'stories',
      kidsLabel: t('kidsNav.stories', 'Bedtime Stories'),
      isHighlight: true
    },
    // ADDED: Tajweed Coach for Kids Paradise (2026-04-25)
    { 
      path: '/quran?tajweed=true', 
      label: t('nav.tajweedCoach', 'Tajweed Coach'), 
      icon: GraduationCap, 
      isPro: true, 
      featureId: 'tajweed_coach',
      id: 'tajweed-coach',
      kidsLabel: t('kidsNav.tajweedCoach', 'Quran Coach')
    },
    { 
      path: '/halal-scanner', 
      label: t('nav.halalScanner', 'Halal Scanner'), 
      icon: Scan, 
      isPro: true, 
      featureId: 'halal_scanner',
      isHighlight: true,
      id: 'halal-scanner',
      kidsLabel: t('kidsNav.halalScanner', 'Food Checker')
    },
    { 
      path: '/progress', 
      label: t('nav.progress', 'Progress'), 
      icon: TrendingUp, 
      isPro: true, 
      featureId: 'reading_stats',
      id: 'progress',
      kidsLabel: t('kidsNav.progress', 'My Stars')
    },
    { 
      path: '/quran/zoom/1', 
      label: t('nav.zoom', 'Zoom'), 
      icon: ZoomIn, 
      isPro: true, 
      featureId: 'zoom_mode',
      isZoom: true,
      id: 'zoom',
      kidsLabel: t('kidsNav.zoom', 'Big Letters')
    },
    { 
      path: '/qibla', 
      label: t('nav.qibla', 'Qibla'), 
      icon: Compass, 
      id: 'qibla',
      kidsLabel: t('kidsNav.qibla', 'Find Qibla')
    },
    { 
      path: '/bookmarks', 
      label: t('nav.saved', 'Saved'), 
      icon: Bookmark, 
      id: 'saved',
      kidsLabel: t('kidsNav.saved', 'My Favorites')
    },
    // Search hidden as per user request
    { 
      path: '/settings', 
      label: t('nav.settings', 'Settings'), 
      icon: Settings, 
      id: 'settings',
      kidsLabel: t('kidsNav.settings', 'Settings')
    }
  ];
};

/**
 * KidsMenu Component
 * Pixel-perfect match to QuranPage menu structure
 * 
 * @param {string} variant - 'sidebar' | 'dock' | 'sheet'
 * @param {boolean} useKidsLabels - Use kid-friendly labels
 */
export const KidsMenu = ({ 
  variant = 'sidebar',
  className = '',
  onItemClick = null,
  showLabels = true,
  useKidsLabels = true
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPro, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();

  const navItems = useMemo(() => getKidsNavItems(t), [t]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/quran') return location.pathname === '/quran' || location.pathname.match(/^\/quran\/\d+$/);
    if (path.includes('/zoom/')) return location.pathname.includes('/zoom');
    return location.pathname.startsWith(path.split('#')[0].split('?')[0]);
  };

  const handleNavClick = (e, item) => {
    const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
    
    if (isItemLocked) {
      e.preventDefault();
      requestFeatureAccess(
        { id: item.featureId, name: item.label },
        () => { navigate(item.path); }
      );
      return;
    }
    
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const getLabel = (item) => useKidsLabels ? item.kidsLabel : item.label;

  // Kids-themed colors for active states
  const kidsActiveColors = {
    home: 'bg-gradient-to-r from-pink-400 to-pink-500',
    quran: 'bg-gradient-to-r from-emerald-400 to-emerald-500',
    prayer: 'bg-gradient-to-r from-blue-400 to-blue-500',
    duas: 'bg-gradient-to-r from-purple-400 to-purple-500',
    learn: 'bg-gradient-to-r from-amber-400 to-amber-500',
    'halal-scanner': 'bg-gradient-to-r from-green-400 to-green-500',
    progress: 'bg-gradient-to-r from-yellow-400 to-orange-400',
    zoom: 'bg-gradient-to-r from-cyan-400 to-cyan-500',
    qibla: 'bg-gradient-to-r from-indigo-400 to-indigo-500',
    saved: 'bg-gradient-to-r from-red-400 to-red-500',
    search: 'bg-gradient-to-r from-teal-400 to-teal-500',
    settings: 'bg-gradient-to-r from-gray-400 to-gray-500',
  };

  // Dock variant (bottom navigation) - EXACT match to QuranPage
  if (variant === 'dock') {
    return (
      <div 
        className={`flex items-center gap-1 overflow-x-auto scrollbar-hide snap-x snap-mandatory ${className}`}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          maxWidth: '280px',
          margin: '0 auto',
          paddingLeft: '8px',
          paddingRight: '8px',
        }}
        data-testid="kids-menu-dock"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
          
          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <Link
                  to={isItemLocked ? '#' : item.path}
                  onClick={(e) => handleNavClick(e, item)}
                  data-testid={`kids-dock-nav-${item.id}`}
                  className={`flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300 snap-center relative ${
                    active 
                      ? `${kidsActiveColors[item.id] || 'bg-primary'} text-white shadow-lg scale-110` 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon className={`h-6 w-6 ${active ? 'stroke-[2.5]' : ''}`} />
                  {isItemLocked && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                      <Star className="h-2.5 w-2.5 text-white fill-white" />
                    </span>
                  )}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                {getLabel(item)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  // Sidebar variant - EXACT match to QuranPage sidebar structure
  return (
    <nav className={`flex flex-col gap-1.5 ${className}`} data-testid="kids-menu-sidebar">
      {navItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path);
        const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));

        return (
          <Tooltip key={item.id}>
            <TooltipTrigger asChild>
              <Link
                to={isItemLocked ? '#' : item.path}
                onClick={(e) => handleNavClick(e, item)}
                data-testid={`kids-sidebar-nav-${item.id}`}
                className={`group flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200 relative ${
                  active
                    ? `${kidsActiveColors[item.id] || 'bg-primary'} text-white shadow-lg`
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                } ${item.isHighlight && !active ? 'ring-2 ring-amber-400/50 ring-offset-2' : ''}`}
              >
                <div className={`p-1.5 rounded-xl ${active ? 'bg-white/20' : 'bg-muted/30'}`}>
                  <Icon className={`h-5 w-5 ${active ? 'stroke-[2.5]' : ''}`} />
                </div>
                {showLabels && (
                  <span className="font-semibold text-sm">{getLabel(item)}</span>
                )}
                {isItemLocked && (
                  <span className="ml-auto">
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                  </span>
                )}
                {item.isHighlight && !active && (
                  <Sparkles className="h-4 w-4 text-amber-500 ml-auto animate-bounce" />
                )}
              </Link>
            </TooltipTrigger>
            {!showLabels && (
              <TooltipContent side="right" className="font-medium">
                {getLabel(item)}
              </TooltipContent>
            )}
          </Tooltip>
        );
      })}
    </nav>
  );
};

export default KidsMenu;
